import { type Stripe } from '@stripe/stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';

import { reportErrorToBugsnag } from '@air/utils-error';

let stripePromise: Promise<Stripe | null> | null = null;

/**
 * Lazily loads the Stripe.js script and returns a memoized promise for the loaded instance.
 * Returns `null` (instead of a promise) when `NEXT_PUBLIC_STRIPE_KEY` is missing, so callers
 * never inject the Stripe script on pages that don't need it. Must not be called at module
 * scope — only from inside an effect or event handler.
 *
 * Imports `loadStripe` from the `@stripe/stripe-js/pure` entry rather than the package root:
 * the default entry schedules its own script injection as a side effect of being imported
 * (`getStripePromise()` runs via a resolved `Promise.resolve().then()` at module evaluation),
 * which would load Stripe.js on every page that imports this module, not just pages that call
 * this function. The `pure` entry exports the same `loadStripe` without that side effect.
 *
 * Retries on failure: if the Stripe.js script is blocked or fails to load, `loadStripe` rejects.
 * Without a catch here, that rejection would sit memoized in `stripePromise` forever, so every
 * later mount would reuse the same dead promise instead of retrying — and since `<Elements
 * stripe={promise}>` only attaches a fulfillment handler, the rejection would go unhandled. The
 * catch below reports the failure to Bugsnag, drops the memoized promise so the next call starts
 * a fresh `loadStripe` (the `pure` loader resets its own internal script-injection cache on
 * failure, so the retry actually re-injects the script), and resolves to `null` so callers see
 * the same "not loaded" state as before a key was ever provided.
 */
export const getStripePromise = (): Promise<Stripe | null> | null => {
  const apiKey = process.env.NEXT_PUBLIC_STRIPE_KEY;

  if (!apiKey) {
    console.error('No Stripe key was provided! Missing the NEXT_PUBLIC_STRIPE_KEY environment variable.');
    return null;
  }

  stripePromise ??= loadStripe(apiKey).catch((error: unknown) => {
    reportErrorToBugsnag({ error, context: 'Failed to load Stripe.js' });
    stripePromise = null;
    return null;
  });

  return stripePromise;
};
